import React, { useEffect, useMemo, useState } from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import './LoginPage.css';

const COGNITO_AUTHORITY = process.env.REACT_APP_COGNITO_AUTHORITY;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const REDIRECT_AFTER_LOGIN = process.env.REACT_APP_COGNITO_POST_LOGIN_REDIRECT || '/';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const USER_API_BASE = process.env.REACT_APP_USER_API_BASE_URL || '';

function deriveUserPoolId(authority) {
  if (!authority) {
    return '';
  }

  try {
    const url = new URL(authority);
    return url.pathname.replace(/^\//, '');
  } catch (error) {
    return authority.split('/').pop() || '';
  }
}

const COGNITO_USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || deriveUserPoolId(COGNITO_AUTHORITY);

function LoginPage({ initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [signedInUser, setSignedInUser] = useState('');

  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  

  const [registerForm, setRegisterForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    birthdate: '',
    gender: '',
    password: '',
    confirmPassword: '',
  });

  const [confirmForm, setConfirmForm] = useState({
    username: '',
    code: '',
  });

  const userPool = useMemo(() => {
    if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID) {
      return null;
    }

    return new CognitoUserPool({
      UserPoolId: COGNITO_USER_POOL_ID,
      ClientId: COGNITO_CLIENT_ID,
    });
  }, []);

  useEffect(() => {
    const pathname = mode === 'register' ? '/register' : '/login';
    if (window.location.pathname !== pathname) {
      window.history.replaceState({}, document.title, pathname);
    }
  }, [mode]);

  useEffect(() => {
    if (!userPool) {
      return;
    }

    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      return;
    }

    currentUser.getSession((sessionError, session) => {
      if (sessionError || !session?.isValid()) {
        return;
      }

      setSignedInUser(currentUser.getUsername());
      setMessage('You are already signed in. Redirecting...');
      window.setTimeout(() => {
        window.location.assign(REDIRECT_AFTER_LOGIN);
      }, 600);
    });
  }, [userPool]);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;

    let mounted = true;

    function initRecaptcha() {
      if (!mounted) return;
      try {
        if (window.grecaptcha && window.grecaptcha.render) {
          const id = window.grecaptcha.render('recaptcha-container', {
            sitekey: RECAPTCHA_SITE_KEY,
            size: 'invisible',
            callback: (token) => {
              setRecaptchaToken(token);
            },
          });

          setRecaptchaWidgetId(id);
        }
      } catch (e) {
        // ignore
      }
    }

    if (window.grecaptcha && window.grecaptcha.render) {
      initRecaptcha();
    } else {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => initRecaptcha();
      document.body.appendChild(script);

      return () => {
        mounted = false;
        try { document.body.removeChild(script); } catch (e) {}
      };
    }
  }, []);

  function clearFeedback() {
    setError('');
    setMessage('');
  }

  function switchMode(nextMode) {
    clearFeedback();
    setMode(nextMode);
  }

  function handleLoginFieldChange(event) {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
  }

  function handleRegisterFieldChange(event) {
    const { name, value } = event.target;
    setRegisterForm((current) => ({ ...current, [name]: value }));
  }

  function handleConfirmFieldChange(event) {
    const { name, value } = event.target;
    setConfirmForm((current) => ({ ...current, [name]: value }));
  }

  async function handleSignIn(event) {
    event.preventDefault();
    clearFeedback();

    if (!userPool) {
      setError('Cognito configuration is incomplete.');
      return;
    }

    // If reCAPTCHA is configured, require token before authenticating
    if (RECAPTCHA_SITE_KEY) {
      if (!recaptchaToken) {
        if (window.grecaptcha && recaptchaWidgetId !== null) {
          try {
            window.grecaptcha.execute(recaptchaWidgetId);
            return;
          } catch (e) {
            setError('Captcha failed to run. Please try again.');
            return;
          }
        }

        setError('Captcha is loading. Please wait and try again.');
        return;
      }
      // If a backend is configured, verify the token server-side before authenticating
      if (USER_API_BASE) {
        try {
          const verifyRes = await fetch(`${USER_API_BASE.replace(/\/$/, '')}/verify-recaptcha`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: recaptchaToken }),
          });

          const verifyJson = await verifyRes.json().catch(() => ({}));
          if (!verifyRes.ok || !verifyJson.success) {
            setError(verifyJson.message || 'Captcha verification failed.');
            return;
          }
        } catch (err) {
          setError('Captcha verification failed; try again.');
          return;
        }
      }
    }

    setIsLoading(true);

    const authDetails = new AuthenticationDetails({
      Username: loginForm.username,
      Password: loginForm.password,
    });

    const cognitoUser = new CognitoUser({
      Username: loginForm.username,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: () => {
        setSignedInUser(loginForm.username);
        setMessage('Sign in successful. Redirecting...');
        setIsLoading(false);
        // reset captcha state
        setRecaptchaToken('');
        try { if (window.grecaptcha && recaptchaWidgetId !== null && window.grecaptcha.reset) window.grecaptcha.reset(recaptchaWidgetId); } catch (e) {}
        window.setTimeout(() => {
          window.location.assign(REDIRECT_AFTER_LOGIN);
        }, 500);
      },
      onFailure: (authError) => {
        setError(authError?.message || 'Unable to sign in.');
        setIsLoading(false);
        setRecaptchaToken('');
        try { if (window.grecaptcha && recaptchaWidgetId !== null && window.grecaptcha.reset) window.grecaptcha.reset(recaptchaWidgetId); } catch (e) {}
      },
      newPasswordRequired: () => {
        setError('This account requires a new password before it can be used.');
        setIsLoading(false);
        setRecaptchaToken('');
        try { if (window.grecaptcha && recaptchaWidgetId !== null && window.grecaptcha.reset) window.grecaptcha.reset(recaptchaWidgetId); } catch (e) {}
      },
    });
  }

  function handleRegister(event) {
    event.preventDefault();
    clearFeedback();

    if (!userPool) {
      setError('Cognito configuration is incomplete.');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!registerForm.birthdate) {
      setError('Birthdate is required.');
      return;
    }

    if (!registerForm.gender) {
      setError('Gender is required.');
      return;
    }

    if (!registerForm.firstName.trim() || !registerForm.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }

    setIsLoading(true);

    const firstName = registerForm.firstName.trim();
    const lastName = registerForm.lastName.trim();
    const formattedName = `${firstName} ${lastName}`.trim();

    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: registerForm.email }),
      new CognitoUserAttribute({ Name: 'name', Value: formattedName }),
      new CognitoUserAttribute({ Name: 'birthdate', Value: registerForm.birthdate }),
      new CognitoUserAttribute({ Name: 'gender', Value: registerForm.gender }),
      new CognitoUserAttribute({ Name: 'preferred_username', Value: registerForm.username }),
    ];

    if (firstName) {
      attributes.push(new CognitoUserAttribute({ Name: 'given_name', Value: firstName }));
    }

    if (lastName) {
      attributes.push(new CognitoUserAttribute({ Name: 'family_name', Value: lastName }));
    }

    userPool.signUp(registerForm.username, registerForm.password, attributes, [], (signUpError) => {
      setIsLoading(false);

      if (signUpError) {
        setError(signUpError.message || 'Unable to create account.');
        return;
      }

      setConfirmForm({ username: registerForm.username, code: '' });
      setMode('confirm');
      setMessage('Account created. Enter the verification code sent to your email.');
    });
  }

  function handleConfirm(event) {
    event.preventDefault();
    clearFeedback();

    if (!userPool) {
      setError('Cognito configuration is incomplete.');
      return;
    }

    setIsLoading(true);

    const cognitoUser = new CognitoUser({
      Username: confirmForm.username,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(confirmForm.code, true, (confirmError) => {
      setIsLoading(false);

      if (confirmError) {
        setError(confirmError.message || 'Unable to verify account.');
        return;
      }

      setLoginForm((current) => ({ ...current, username: confirmForm.username }));
      setMode('login');
      setMessage('Account verified. You can now sign in.');
    });
  }

  function handleBackHome() {
    window.location.assign('/');
  }

  if (!userPool) {
    return (
      <section className="login-page-wrapper" aria-labelledby="login-page-title">
        <div className="login-shell">
          <div className="login-panel login-panel-brand">
            <div className="login-brand-badge">Derek Downs</div>
            <h1 className="login-hero-title">Secure account access</h1>
            <p className="login-hero-text">Create an account or sign in to access protected features on the site.</p>
          </div>
          <div className="login-panel login-panel-form">
            <div className="login-header-row">
              <div></div>
              <button type="button" className="login-back-link" onClick={handleBackHome}>
                Back home
              </button>
            </div>
            <h1 id="login-page-title" className="login-title">Sign In</h1>
            <p className="login-subtitle">Cognito configuration is missing.</p>
            <p className="login-message login-error" role="alert">
              Set `REACT_APP_COGNITO_CLIENT_ID` and `REACT_APP_COGNITO_USER_POOL_ID`, or provide a valid `REACT_APP_COGNITO_AUTHORITY`.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="login-page-wrapper" aria-labelledby="login-page-title">
      <div className="login-shell">
        <div className="login-panel login-panel-brand">
          <div className="login-brand-badge">Derek Downs</div>
          <h1 className="login-hero-title">Traditional account access</h1>
          <p className="login-hero-text">
            Sign in with your username and password, or create a new account with Cognito-backed storage.
          </p>
          {signedInUser && <p className="login-signed-in">Signed in as {signedInUser}</p>}
        </div>

        <div className="login-panel login-panel-form">
          <div className="login-header-row">
            <div></div>
            <button type="button" className="login-back-link" onClick={handleBackHome}>
              Back home
            </button>
          </div>
          <div className="login-tablist" role="tablist" aria-label="Authentication options">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              className={`login-tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Sign In
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'register'}
              className={`login-tab ${mode === 'register' ? 'active' : ''}`}
              onClick={() => switchMode('register')}
            >
              Register
            </button>
          </div>

          <h1 id="login-page-title" className="login-title">{mode === 'register' ? 'Create Account' : mode === 'confirm' ? 'Verify Account' : 'Welcome Back'}</h1>
          <p className="login-subtitle">
            {mode === 'register'
              ? 'Register a new account with your details.'
              : mode === 'confirm'
                ? 'Enter the verification code sent to your email.'
                : 'Sign in using your username and password.'}
          </p>

          {mode === 'login' && (
            <form className="login-form" onSubmit={handleSignIn}>
              <label className="login-label" htmlFor="login-username">Username</label>
              <input
                id="login-username"
                name="username"
                type="text"
                value={loginForm.username}
                onChange={handleLoginFieldChange}
                className="login-input"
                placeholder="Enter your username"
                autoComplete="username"
                required
              />

              <label className="login-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                name="password"
                type="password"
                value={loginForm.password}
                onChange={handleLoginFieldChange}
                className="login-input"
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />

              {/* reCAPTCHA container for invisible widget (added when site key configured) */}
              <div id="recaptcha-container" />

              <button type="submit" className="login-submit" disabled={isLoading}>
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          )}

          {mode === 'register' && (
            <form className="login-form" onSubmit={handleRegister}>
              <label className="login-label" htmlFor="register-username">Username</label>
              <input
                id="register-username"
                name="username"
                type="text"
                value={registerForm.username}
                onChange={handleRegisterFieldChange}
                className="login-input"
                placeholder="Choose a username"
                autoComplete="username"
                required
              />

              <div className="login-grid">
                <div>
                  <label className="login-label" htmlFor="register-first-name">First Name</label>
                  <input
                    id="register-first-name"
                    name="firstName"
                    type="text"
                    value={registerForm.firstName}
                    onChange={handleRegisterFieldChange}
                    className="login-input"
                    placeholder="First name"
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div>
                  <label className="login-label" htmlFor="register-last-name">Last Name</label>
                  <input
                    id="register-last-name"
                    name="lastName"
                    type="text"
                    value={registerForm.lastName}
                    onChange={handleRegisterFieldChange}
                    className="login-input"
                    placeholder="Last name"
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <label className="login-label" htmlFor="register-email">Email</label>
              <input
                id="register-email"
                name="email"
                type="email"
                value={registerForm.email}
                onChange={handleRegisterFieldChange}
                className="login-input"
                placeholder="you@example.com"
                autoComplete="email"
                required
              />

              <div className="login-grid">
                <div>
                  <label className="login-label" htmlFor="register-birthdate">Birthdate</label>
                  <input
                    id="register-birthdate"
                    name="birthdate"
                    type="date"
                    value={registerForm.birthdate}
                    onChange={handleRegisterFieldChange}
                    className="login-input"
                    required
                  />
                </div>
                <div>
                  <label className="login-label" htmlFor="register-gender">Gender</label>
                  <select
                    id="register-gender"
                    name="gender"
                    value={registerForm.gender}
                    onChange={handleRegisterFieldChange}
                    className="login-input"
                    required
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="login-grid">
                <div>
                  <label className="login-label" htmlFor="register-password">Password</label>
                  <input
                    id="register-password"
                    name="password"
                    type="password"
                    value={registerForm.password}
                    onChange={handleRegisterFieldChange}
                    className="login-input"
                    placeholder="Create a password"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div>
                  <label className="login-label" htmlFor="register-confirm-password">Confirm Password</label>
                  <input
                    id="register-confirm-password"
                    name="confirmPassword"
                    type="password"
                    value={registerForm.confirmPassword}
                    onChange={handleRegisterFieldChange}
                    className="login-input"
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <button type="submit" className="login-submit" disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          {mode === 'confirm' && (
            <form className="login-form" onSubmit={handleConfirm}>
              <label className="login-label" htmlFor="confirm-username">Username</label>
              <input
                id="confirm-username"
                name="username"
                type="text"
                value={confirmForm.username}
                onChange={handleConfirmFieldChange}
                className="login-input"
                placeholder="Your username"
                autoComplete="username"
                required
              />

              <label className="login-label" htmlFor="confirm-code">Verification Code</label>
              <input
                id="confirm-code"
                name="code"
                type="text"
                value={confirmForm.code}
                onChange={handleConfirmFieldChange}
                className="login-input"
                placeholder="Enter verification code"
                required
              />

              <button type="submit" className="login-submit" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify Account'}
              </button>
              <button type="button" className="login-link-button" onClick={() => switchMode('login')}>
                Back to sign in
              </button>
            </form>
          )}

          <p className="login-message login-error" role="alert">
            {error}
          </p>
          {message && <p className="login-message login-success" role="status">{message}</p>}

          {mode !== 'confirm' && (
            <button
              type="button"
              className="login-link-button"
              onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
            >
              {mode === 'login' ? 'Need an account? Register here' : 'Already have an account? Sign in'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export default LoginPage;
